import {
  OUTLOOK_HR_STYLES,
  OUTLOOK_QUOTE_STYLES,
  QUOTE_CONTAINER_SELECTORS,
  QUOTE_SIBLING_SELECTORS,
  QUOTE_START_SELECTORS,
} from './emailQuotes.constants';
import { matchesQuoteStyle } from './matchesQuoteStyle';

const quoteContainerSelector = QUOTE_CONTAINER_SELECTORS.join(', ');
const quoteStartSelector = QUOTE_START_SELECTORS.join(', ');
const quoteSiblingSelector = QUOTE_SIBLING_SELECTORS.join(', ');

/** Removes the white space from a style, so that spacing does not affect matching. */
function removeWhiteSpace(style: string): string {
  return style.replace(/\s+/g, '');
}

const quoteStyles = OUTLOOK_QUOTE_STYLES.map(removeWhiteSpace);
const hrStyles = OUTLOOK_HR_STYLES.map(removeWhiteSpace);

/** Checks if an element and its next siblings are quoted content. */
function startsQuote(element: Element): boolean {
  if (element.matches(quoteStartSelector)) {
    return true;
  }

  const style = getStyle(element);

  if (matchesQuoteStyle(style, quoteStyles)) {
    return true;
  }

  if (
    element.tagName.toLowerCase() === 'hr' &&
    matchesQuoteStyle(style, hrStyles)
  ) {
    return true;
  }

  return (
    element.children.length === 1 &&
    matchesQuoteStyle(getStyle(element.children[0]), quoteStyles)
  );
}

/** The style of an element, in the form the quote styles are matched in. */
function getStyle(element: Element): string | null {
  const style = element.getAttribute('style');

  return style === null ? null : removeWhiteSpace(style);
}

/** Checks if an element's content is quoted. */
function containsQuote(element: Element): boolean {
  return (
    element.matches(quoteContainerSelector) ||
    Array.from(element.children).some((child) =>
      child.matches(quoteSiblingSelector),
    )
  );
}

/** Removes a node together with its next siblings. */
function removeWithNextSiblings(node: Node): void {
  for (let current: Node | null = node; current; ) {
    const next = current.nextSibling;
    current.parentNode?.removeChild(current);
    current = next;
  }
}

/**
 * Removes, in place, the quoted replies that `replaceReplyQuote` folds when
 * rendering a message.
 *
 * @param root The element holding the email body.
 */
export function stripQuotesFrom(root: Element): void {
  // Iterative, as emails nest deep enough to overflow the call stack.
  const stack: Element[] = [root];

  for (let parent = stack.pop(); parent; parent = stack.pop()) {
    for (const child of Array.from(parent.children)) {
      if (startsQuote(child)) {
        removeWithNextSiblings(child);
        break;
      }

      if (containsQuote(child)) {
        child.remove();
        continue;
      }

      stack.push(child);
    }
  }
}

/**
 * Removes the quoted replies from an email body.
 *
 * @param html The email body html.
 */
export function stripEmailQuotes(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  stripQuotesFrom(doc.body);

  return doc.body.innerHTML.trim();
}
